/**
 * API Client for AkountFly
 * 
 * Token-based authentication:
 * - Access token stored in localStorage, sent as Bearer header
 * - Refresh token stored in localStorage, sent in request body
 * - CSRF token support for additional protection
 * - Automatic token refresh on 401
 */
import axios from 'axios';
import { authUrlWithReturnTo } from './postLoginRedirect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// "Report this issue" - diagnostics + repeat-failure nudge state.
// The nudge is intentionally quiet: it must see multiple identical 5xx errors
// on the SAME endpoint AND the user must have interacted with the page
// recently (background polls that fail silently should never surface a toast).
import { toast as _reportToast } from 'sonner';
import { recordApiError, reportIssue } from './diagnosticsBuffer';
// Endpoint → [timestamps of recent 5xx failures]
const _serverFailures = new Map();
let _lastReportToastAt = 0;
// Once the user dismisses the report toast, don't show it again in this
// browser session. Prevents "keeps failing" spam described in feedback.
let _reportToastDismissed = false;
// Endpoints that fail invisibly in the background - never worth pestering
// the user about because they have no matching UI feedback.
// iter1244 · Bank/Mono polling endpoints frequently transient-500 during
// Mongo pool recycles or pod restarts. Adding them so a temporary
// backend hiccup doesn't summon the alarming "Something keeps failing"
// toast on pages where the user is not observing any broken UI.
const _SILENT_ENDPOINTS = [
  '/api/notifications',
  '/api/billing/usage',
  '/api/billing/notifications',
  '/api/health',
  '/api/analytics/pageview',
  '/api/analytics/event',
  '/api/telemetry',
  '/api/backlog',
  '/api/websocket',
  '/api/activity/unread-counts',
  '/api/open-banking-feeds/mono/catch-up-status',
  '/api/banking/backlog-jobs/active',
  '/api/bank-accounts/active-processing',
  '/api/bank-feeds/notifications',
  '/api/bank-feeds/upload-job-status',
  '/api/bank-feeds/',  // catches /sync-progress and other bank-feed polls
  '/api/user-journey/priority-tasks',
  '/api/open-tasks/aggregated',
  '/api/ai-training/weekly-stats',
  '/api/background-jobs',
];
// iter1244 · Any GET whose path OR query matches these patterns is a
// background poll and should NEVER trigger the report toast. Matched
// alongside `_SILENT_ENDPOINTS` at the exact-prefix level.
const _POLL_PATH_HINTS = ['/status/', '/sync-progress', '/progress', '/poll'];
// Consider the user "active" if there was any pointer/keyboard input in the
// last 20 seconds. Silent background failures shouldn't wake the user up.
let _lastUserInteractionAt = Date.now();
if (typeof window !== 'undefined') {
  const _mark = () => { _lastUserInteractionAt = Date.now(); };
  window.addEventListener('pointerdown', _mark, { passive: true });
  window.addEventListener('keydown', _mark, { passive: true });
  window.addEventListener('touchstart', _mark, { passive: true });
}

// Smart URL resolution: use relative paths in production to avoid cross-origin issues.
// In Emergent's architecture, frontend and backend share the same domain via ingress,
// so relative URLs (/api/...) always work regardless of REACT_APP_BACKEND_URL value.
const isLocalDev = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
export const API_BASE = isLocalDev ? `${BACKEND_URL}/api` : '/api';

// Storage keys
const TOKEN_KEY = 'akountfly_token';
const REFRESH_TOKEN_KEY = 'akountfly_refresh_token';
const CSRF_KEY = 'akountfly_csrf';
// iter-bugfix (refresh-now-csrf): the backend also sets the CSRF token as a
// non-httpOnly cookie (`akountfly_csrf`) so the frontend can read it even
// after localStorage has been cleared (new tab, storage quota, manual
// dev-tools wipe). Reading from BOTH sources eliminates the "Could not
// start refresh - please try again" failures that happened whenever the
// cookie was present but localStorage was empty or stale.
const CSRF_COOKIE_NAME = 'akountfly_csrf';

/**
 * Read the CSRF cookie value directly from document.cookie. Returns null
 * if the cookie is absent. Safe to call in SSR contexts (window/document
 * are guarded).
 */
const readCsrfCookie = () => {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const escaped = CSRF_COOKIE_NAME.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]+)'));
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Get CSRF token - prefers localStorage, falls back to the non-httpOnly
 * cookie. Whichever is present is returned; if localStorage and cookie
 * disagree, the cookie is the source of truth (the backend rotates it on
 * /auth/refresh, but a stray axios instance may have missed updating
 * localStorage).
 */
export const getCsrfToken = () => {
  const cookieToken = readCsrfCookie();
  if (cookieToken) {
    // Keep localStorage in sync so the next read is cheap.
    try { localStorage.setItem(CSRF_KEY, cookieToken); } catch (_) { /* ignore quota */ }
    return cookieToken;
  }
  return localStorage.getItem(CSRF_KEY);
};

/**
 * Store CSRF token
 */
export const storeCsrfToken = (token) => {
  if (token) {
    localStorage.setItem(CSRF_KEY, token);
  }
};

/**
 * Create configured axios instance for API calls.
 *
 * Phase 3 (Feb 2026): the SOLE auth carrier is the httpOnly `access_token`
 * cookie issued by the backend on login/register/refresh. Cookies are
 * sent automatically because `withCredentials: true` is set below.
 * No Bearer header. No access/refresh tokens in localStorage. The only
 * thing JavaScript still touches is the non-httpOnly CSRF token cookie,
 * which is used for the double-submit `X-CSRF-Token` header pattern.
 */
const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 120000,
  withCredentials: true,  // ← send httpOnly auth cookies on every request
  headers: {
    'Content-Type': 'application/json'
  }
});

// Extended timeout for long-running operations
export const LONG_OPERATION_TIMEOUT = 300000;

// Request interceptor - add auth headers
apiClient.interceptors.request.use((config) => {
  // Phase 3: cookie is the SOLE auth carrier. Bearer-from-localStorage
  // fallback is gone - httpOnly access_token cookie is sent automatically
  // because withCredentials: true is set on this client.

  // Add CSRF token for state-changing requests (double-submit cookie
  // pattern). The header is harmless on GETs but cheap to skip.
  const mutationMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
  if (mutationMethods.includes(config.method?.toUpperCase())) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }

  // iOS App Store build: identify the client so the backend applies
  // iOS-safe behaviour (e.g. backlog ingestion is always the free path).
  try {
    const { isIOSNative, isNativeApp, nativePlatform } = require('./nativeBridge');
    if (isIOSNative()) config.headers['X-Client-Platform'] = 'ios';
    else if (isNativeApp() && nativePlatform()) config.headers['X-Client-Platform'] = String(nativePlatform()).toLowerCase();
    const standalone = (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true;
    if (standalone) config.headers['X-Client-Display'] = 'standalone';
  } catch { /* bridge helper unavailable - browser build */ }

  return config;
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor - handle 401 with token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Store CSRF token if present in response
    if (response.data?.csrf_token) {
      storeCsrfToken(response.data.csrf_token);
    }
    return response;
  },
  async (error) => {
    // Sprint 1 F1.9 - normalise the API error envelope so call-sites can
    // read `err.normalised.message` regardless of which shape the backend
    // returned ({detail}, {message}, {error}, {errors:[]}).
    const _payload = error.response?.data || {};
    const _fieldErrors = (
      _payload.errors ||
      _payload.field_errors ||
      (Array.isArray(_payload.detail) ? _payload.detail : null) ||
      null
    );
    error.normalised = {
      status: error.response?.status || 0,
      code: _payload.code || _payload.error_code
            || (_payload.detail && typeof _payload.detail === 'object' && _payload.detail.error_code)
            || null,
      message: (
        (typeof _payload.detail === 'string' && _payload.detail) ||
        // iter1113 - backend guards return detail as an object:
        // {"error_code": "bank_account_archived", "message": "...", ...}.
        // Prefer detail.message so guarded 400s surface human copy.
        (_payload.detail && typeof _payload.detail === 'object' && _payload.detail.message) ||
        _payload.message ||
        _payload.error ||
        error.message ||
        'Something went wrong'
      ),
      fieldErrors: _fieldErrors,
      raw: _payload,
    };

    // "Report this issue" diagnostics - record every API failure; nudge a
    // report toast when the SAME endpoint fails server-side twice in 2 min.
    try {
      const _path = (error.config?.url || '').split('?')[0];
      recordApiError({
        method: error.config?.method,
        url: _path,
        status: error.normalised.status,
        message: error.normalised.message,
      });
      if (error.normalised.status >= 500) {
        const _now = Date.now();
        // Ignore silent background endpoints - they never earn a nudge.
        const _isSilent = _SILENT_ENDPOINTS.some((p) => _path.startsWith(p));
        // iter1244 · Additional guard: any GET whose path resembles a
        // background poll (status/progress/poll) is treated as silent,
        // even if not explicitly listed above. GET polls fail invisibly
        // during transient Mongo hiccups; showing "Something keeps
        // failing" on a page where the user sees nothing broken is
        // alarming with no actionable value.
        const _isPollShape = (error.config?.method || '').toLowerCase() === 'get'
          && _POLL_PATH_HINTS.some((h) => _path.includes(h));
        // Ignore if the user hasn't touched the page in the last 20s.
        const _userRecentlyActive = _now - _lastUserInteractionAt < 20000;
        // Track the last 5 failure timestamps for this endpoint.
        const _hist = _serverFailures.get(_path) || [];
        _hist.push(_now);
        // Keep only failures from the last 90 seconds.
        const _recent = _hist.filter((t) => _now - t < 90000);
        _serverFailures.set(_path, _recent);
        // Nudge only when: 5+ failures in 90s on the same non-silent,
        // non-poll endpoint, the user is actively interacting, no
        // toast in the last 10 minutes, and the user hasn't already
        // dismissed this session. iter1244 · raised threshold (was 3
        // in 60s) after users reported the toast surfacing on pages
        // where nothing visible was broken - a symptom of counting
        // background polls that transient-500 during Mongo hiccups.
        if (
          !_reportToastDismissed &&
          !_isSilent &&
          !_isPollShape &&
          _userRecentlyActive &&
          _recent.length >= 5 &&
          _now - _lastReportToastAt > 600000
        ) {
          _lastReportToastAt = _now;
          _reportToast.error('Something keeps failing on this page', {
            description: 'Send a one-click report - we attach the technical details automatically.',
            action: {
              label: 'Report',
              onClick: () => reportIssue(
                { trigger: 'api_failure', endpoint: _path, status: error.normalised.status, message: error.normalised.message },
                `api_failure:${_path}`
              ),
            },
            onDismiss: () => { _reportToastDismissed = true; },
            duration: 8000,
          });
        }
      }
    } catch (_) { /* never break the error flow */ }

    const originalRequest = error.config;
    
    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry for auth endpoints
      if (originalRequest.url?.includes('/auth/login') || 
          originalRequest.url?.includes('/auth/register') ||
          originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      // iter1236 - Non-idempotent uploads MUST NOT auto-replay after 401.
      // Re-firing the multipart POST creates a duplicate upload job. See
      // App.js interceptor for the full rationale.
      if (originalRequest.url?.includes('/bank-feeds/upload-background')) {
        error.__uploadRetryBlocked = true;
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Phase 3: refresh runs cookie-only. The httpOnly refresh_token
        // cookie is sent automatically (withCredentials), no body needed.
        const response = await axios.post(`${API_BASE}/auth/refresh`, {}, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            // CSRF double-submit - cookie value is still readable.
            ...(getCsrfToken() ? { 'X-CSRF-Token': getCsrfToken() } : {}),
          },
        });

        // Backend rotates the CSRF cookie on refresh and ALSO returns the
        // new value in the body so we can update the in-memory cache the
        // SPA stamps on subsequent mutating requests. Access + refresh
        // tokens live in httpOnly cookies and are NEVER touched by JS.
        if (response.data?.csrf_token) {
          storeCsrfToken(response.data.csrf_token);
        }

        processQueue(null, true);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // When on a PUBLIC route (landing, /auth, policy pages, magic-link
        // verification, etc.) do NOT clear localStorage and do NOT hard-
        // redirect. These pages don't require an authenticated session and
        // bouncing a guest off the landing page is the same class of bug
        // as the iter 702 auth-loop. Login flow on /auth also owns its
        // own state - wiping localStorage from under it during the
        // post-login /business call caused the onboarding-bounce bug.
        const PUBLIC_PATHS = [
          '/', '/auth', '/login',
          '/accept-invitation', '/verify-email', '/reset-password',
          '/pricing', '/privacy', '/privacy-policy', '/terms',
          '/terms-of-service', '/cookie-policy',
          // iter1343 - Help Center subtree is public (Apple Support URL +
          // knowledge-base browsing without account). MUST stay in sync
          // with the sibling list in src/App.js. The startsWith check
          // below covers /help, /help/:slug, /help/category/:slug.
          '/help',
          // Partner & referral program public routes (iter: Partner Program)
          '/partner', '/partners/apply', '/r',
        ];
        if (typeof window !== 'undefined') {
          const pathname = window.location.pathname;
          const isPublic = PUBLIC_PATHS.some((p) =>
            p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(p + '/')
          );
          if (isPublic) {
            return Promise.reject(refreshError);
          }
        }

        // Off public routes: clear local UI state and bounce to login.
        localStorage.removeItem('akountfly_user');
        localStorage.removeItem('akountfly_business');
        localStorage.removeItem(CSRF_KEY);
        window.location.href = authUrlWithReturnTo();

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;

/**
 * Auth API functions
 */
export const authApi = {
  /**
   * Login with email and password
   */
  login: async (email, password, otpCode = null, accountType = 'business') => {
    const payload = { 
      email, 
      password,
      account_type: accountType
    };
    if (otpCode) {
      payload.otp_code = otpCode;
    }
    
    const response = await apiClient.post('/auth/login', payload);

    // Phase 3: access_token / refresh_token live in httpOnly cookies only.
    // We just cache the readable CSRF token so subsequent mutating axios
    // calls can stamp the `X-CSRF-Token` header.
    if (response.data?.csrf_token) {
      storeCsrfToken(response.data.csrf_token);
    }

    return response.data;
  },
  
  /**
   * Register new user
   */
  register: async (email, password, fullName, accountType = 'business', consentOptions = {}) => {
    const response = await apiClient.post('/auth/register', {
      email,
      password,
      full_name: fullName,
      account_type: accountType,
      // iter959 post-signoff - country of business, captured on the register
      // form so the Day-0 welcome email uses country-aware copy and the
      // onboarding page can pre-select it.
      country: consentOptions.country || null,
      referral_code: consentOptions.referral_code || null,
      terms_accepted: consentOptions.terms_accepted ?? false,
      privacy_accepted: consentOptions.privacy_accepted ?? false,
      ai_processing_consent: consentOptions.ai_processing_consent ?? true,
      marketing_consent: consentOptions.marketing_consent ?? false
    });
    
    // Phase 3: same as login - only the readable CSRF is cached.
    if (response.data?.csrf_token) {
      storeCsrfToken(response.data.csrf_token);
    }

    return response.data;
  },
  
  /**
   * Logout - clears local storage
   */
  logout: async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (e) {
      // Logout endpoint failure is non-fatal - we still clear local state
      // below. Surface to console so it's diagnosable, but don't surface to
      // the user (we've effectively logged them out client-side anyway).
      // eslint-disable-next-line no-console
      console.debug('[auth] /auth/logout failed (proceeding with local clear):', e?.message);
    }
    
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('akountfly_user');
    localStorage.removeItem('akountfly_business');
    localStorage.removeItem(CSRF_KEY);
    // iter1235 - Clear per-user tracker state so a shared device doesn't
    // leak the previous user's background upload jobs or dismissed
    // opening-balance reminders into the next session.
    localStorage.removeItem('akountfly_background_jobs');
    sessionStorage.removeItem('akountfly_ob_banner_dismissed_ids');
    sessionStorage.removeItem('akountfly_nav_history');
  },
  
  /**
   * Logout from all devices
   */
  logoutAll: async () => {
    const response = await apiClient.post('/auth/logout-all');
    
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('akountfly_user');
    localStorage.removeItem('akountfly_business');
    localStorage.removeItem(CSRF_KEY);
    localStorage.removeItem('akountfly_background_jobs');
    sessionStorage.removeItem('akountfly_ob_banner_dismissed_ids');
    sessionStorage.removeItem('akountfly_nav_history');
    
    return response.data;
  },
  
  /**
   * Get current user
   */
  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  
  /**
   * Get active sessions
   */
  getSessions: async () => {
    const response = await apiClient.get('/auth/sessions');
    return response.data;
  },
  
  /**
   * Refresh token manually
   */
  refresh: async () => {
    // Phase 3: cookie-only refresh. Body is empty; the httpOnly
    // refresh_token cookie + X-CSRF-Token header do the work.
    const response = await apiClient.post('/auth/refresh', {});

    if (response.data?.csrf_token) {
      storeCsrfToken(response.data.csrf_token);
    }

    return response.data;
  }
};
